import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Divider, Grid, Title, Table } from "@mantine/core";
import Translation from "../Utils/Translation";
import { getRequest, postRequest } from "../../utils/request/request";
import Number from "../Editor/Fields/belongsTo/Number";
import { IconCalculator, IconPlus, IconTrash } from "@tabler/icons-react";
import ComponentSelect from "../Editor/Fields/belongsTo/ComponentSelect";

type ItemType = {
    id: number,
    name: string,
    qty: number,
    project: {
        elements: Array<{
            id: number,
            project: {
                id: number,
            },
            name: string,
            material: {
                id: number,
                label: string,
            },
            print: string,
            punching_knife: {
                id: number,
                name: string,
                width: number,
                height: number,
                wave_direction: string,
            },
            wave_direction: string,
            multiplier: number,
            width: number,
            height: number,
        }>
    },
};

type AllocationType = { stock: any, piecesPerSheet: number, totalPiecesProduced: number, format: 'normal' | 'rotated', isEnough: boolean, sheetsNeededForExact: number, wastePerSheet: number, wastePercentage: number };

const copy = <T,>(o: T): T => JSON.parse(JSON.stringify(o));

const Stock = () => {
    const [stancList, setStancList] = useState<ItemType['project']['elements']>([]);
    const [stock, setStock] = useState<{id: number | null, token: string | null, value: { width: number, height: number, multiplier: number, material: { id: number, label: string} | null }[]}>({id: null, token: "stock", value: []});
    useEffect(() => {
        getRequest<{
            items: Array<ItemType>
        }>("/components", {
            option: "Worksheet",
            filter: {
                field: "state",
                operator: "IN",
                value: [200, 300],
            },
            view: {
                id: null,
                name: null,
                qty: null,
                project: {
                    elements: {
                        id: null,
                        project: {
                            id: null,
                        },
                        name: null,
                        material: null,
                        print: null,
                        punching_knife: {
                            id: null,
                            name: null,
                            width: null,
                            height: null,
                            wave_direction: null,
                        },
                        wave_direction: null,
                        multiplier: null,
                        width: null,
                        height: null,
                    }
                },
            },
        }).then((res) => {
            setStancList(res.items.flatMap((worksheet) => (worksheet.project?.elements ?? []).filter(({width, height}) => width && height).map((element) => ({ ...element, multiplier: element.multiplier * (worksheet.qty || 0) }))).reduce((prev, next) => {
                const index = prev.findIndex((item) => item.id === next.id);
                if (index >= 0) {
                    prev[index].multiplier += next.multiplier;
                } else {
                    prev.push({ ...next });
                }
                return prev;
            }, [] as ItemType['project']['elements']));

        });
    }, []);
    useEffect(() => {
        getRequest("/components", {
            option: "Setting",
            filter: {
                field: "token",
                value: "stock",
            },
            view: {
                id: null,
                token: null,
                value: null,
            },
        }).then((res) => {
            const stock = res.items[0] ?? {id: null, token: "stock", value: null};
            stock.value = JSON.parse(stock.value ?? "[]");
            setStock(stock);
        });
    }, []);
    useEffect(() => {
        if (!stock.id) return;
        postRequest("/components", {
            action: "Setting.update",
            id: stock.id,
            view: {
                id: null
            },
            data: {
                token: "stock",
                value: JSON.stringify(stock.value),
            }
        })
    }, [stock]);

    const getMaterialKey = (material: any): string => {
        if (!material) return '';
        if (typeof material === 'object') return `${material.id}`;
        return `${material}`;
    };

    const getMaterialLabel = (material: any): string => {
        if (!material) return 'N/A';
        if (typeof material === 'object') return `${material.label ?? material.id ?? ''}`;
        return `${material}`;
    };

    const materials = useMemo(() => {
        const unique = new Map<string, any>();
        stancList.forEach((item: any) => {
            const key = getMaterialKey(item.material);
            if (key) {
                unique.set(key, item.material);
            }
        });
        return Array.from(unique.entries()).map(([key, mat]) => ({
            value: key,
            label: getMaterialLabel(mat),
        }));
    }, [stancList]);

    const [calculationResult, setCalculationResult] = useState<{
        stanc: any,
        allocation: AllocationType
    }[]>([]);

    return (
        <>
            <Grid>
                {stock.value?.map((item, idx) => (
                    <Fragment key={idx}>
                        <Grid.Col span={3}>
                            <ComponentSelect
                                minimumLengthOfSearch={0}
                                config={{
                                    componentName: "Stock",
                                    service: "",
                                    idfield: "id",
                                    fields: {
                                        material: {
                                            datatype: "Option",
                                            combine: {
                                                optionname: "material",
                                                value: "{{value}}",
                                            },
                                            required: true,
                                        }
                                    }
                                }}
                                form={{
                                    values: {
                                        material: item.material
                                    },
                                    errors: {},
                                    setFieldValue: (_, v) => { setStock((stock) => ({...stock, value: stock.value!.map((s, i) => i === idx ? { ...s, material: v } : s)})) },
                                } as any}
                                filter={{ field: "id", operator: "in", value: [...materials?.map(({ value }) => value)] }}
                                action="update"
                                field="material"
                                label="Alapanyag"
                            />
                        </Grid.Col>
                        <Grid.Col span={2}>
                            <Number
                                config={{
                                    componentName: "Stock",
                                    service: "",
                                    idfield: "id",
                                    fields: {
                                        value: {
                                            datatype: "Number",
                                            required: true,
                                        }
                                    }
                                }}
                                form={{
                                    values: {
                                        value: item.width
                                    },
                                    errors: {},
                                    setFieldValue: (_, v) => { setStock((stock) => ({...stock, value: stock.value!.map((s, i) => i === idx ? { ...s, width: v } : s)})) },
                                } as any}
                                action="update"
                                field="value"
                                label="Szélesség"
                            />
                        </Grid.Col>
                        <Grid.Col span={2}>
                            <Number
                                config={{
                                    componentName: "Stock",
                                    service: "",
                                    idfield: "id",
                                    fields: {
                                        value: {
                                            datatype: "Number",
                                            required: true,
                                        }
                                    }
                                }}
                                form={{
                                    values: {
                                        value: item.height
                                    },
                                    errors: {},
                                    setFieldValue: (_, v) => { setStock((stock) => ({...stock, value: stock.value!.map((s, i) => i === idx ? { ...s, height: v } : s)})) },
                                } as any}
                                action="update"
                                field="value"
                                label="Magasság"
                            />
                        </Grid.Col>
                        <Grid.Col span={3}>
                            <Number
                                config={{
                                    componentName: "Stock",
                                    service: "",
                                    idfield: "id",
                                    fields: {
                                        value: {
                                            datatype: "Number",
                                            required: true,
                                        }
                                    }
                                }}
                                form={{
                                    values: {
                                        value: item.multiplier
                                    },
                                    errors: {},
                                    setFieldValue: (_, v) => { setStock((stock) => ({...stock, value: stock.value!.map((s, i) => i === idx ? { ...s, multiplier: v } : s)})) },
                                } as any}
                                action="update"
                                field="value"
                                label="Darabszám"
                            />
                        </Grid.Col>
                        <Grid.Col span={1} style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Button
                                onClick={() => {
                                    setStock((stock) => ({...stock, value: stock.value!.filter((s, i) => i !== idx)}))
                                }}
                            >
                                <IconTrash />
                            </Button>
                        </Grid.Col>
                    </Fragment>
                ))}
            </Grid>
            <Grid>
                <Grid.Col span={12}>
                    <Button
                        onClick={() => {
                            setStock((stock) => ({...stock, value: [...stock.value!, { width: 0, height: 0, multiplier: 1, material: null }]}))
                        }}
                    >
                        <IconPlus />
                    </Button>
                </Grid.Col>
                <Grid.Col span={12}>
                    <Button
                        onClick={() => {
                            const mergedStock = copy(stock.value).reduce((prev, next) => {
                                const key = `${getMaterialKey(next.material)}-${next.width}-${next.height}`;
                                const existing = prev.find((item) => `${getMaterialKey(item.material)}-${item.width}-${item.height}` === key);
                                if (existing) {
                                    existing.multiplier += next.multiplier;
                                } else {
                                    prev.push({ ...next });
                                }
                                return prev;
                            }, [] as typeof stock.value);

                            const res = copy(stancList).map((stanc: any) => {
                                const stancMaterialKey = getMaterialKey(stanc.material);
                                const allocations = copy(mergedStock).filter((s) => {
                                    const stockMaterialKey = getMaterialKey(s.material);
                                    if (stancMaterialKey && stockMaterialKey) return stancMaterialKey === stockMaterialKey;
                                    if (!stancMaterialKey && !stockMaterialKey) return true;
                                    return false;
                                }).map((sheet) => {
                                    if (!stanc.width || !stanc.height || !sheet.width || !sheet.height) {
                                        return {
                                            stock: sheet,
                                            piecesPerSheet: 0,
                                            format: 'normal' as 'normal' | 'rotated',
                                            isEnough: false,
                                            sheetsNeededForExact: 0,
                                            totalPiecesProduced: 0,
                                            wastePerSheet: 0,
                                            wastePercentage: 0,
                                        };
                                    }
                                    const sizeNormal = Math.floor(sheet.width / stanc.width) * Math.floor(sheet.height / stanc.height);
                                    let sizeRotated = 0;
                                    if (!stanc.wave_direction) {
                                        sizeRotated = Math.floor(sheet.width / stanc.height) * Math.floor(sheet.height / stanc.width);
                                    }
                                    const bestSize = Math.max(sizeNormal, sizeRotated);
                                    if (bestSize <= 0) {
                                        return {
                                            stock: sheet,
                                            piecesPerSheet: 0,
                                            format: 'normal' as 'normal' | 'rotated',
                                            isEnough: false,
                                            sheetsNeededForExact: 0,
                                            totalPiecesProduced: 0,
                                            wastePerSheet: 0,
                                            wastePercentage: 0,
                                        };
                                    }
                                    const format = bestSize === sizeRotated && sizeRotated > sizeNormal ? 'rotated' : 'normal';

                                    const totalPiecesProduced = bestSize * sheet.multiplier;
                                    const isEnough = totalPiecesProduced >= stanc.multiplier;
                                    const sheetsNeededForExact = Math.ceil(stanc.multiplier / bestSize);

                                    const sheetArea = sheet.width * sheet.height;
                                    const stancArea = stanc.width * stanc.height;
                                    const wastePerSheet = sheetArea - Math.min(stanc.multiplier, bestSize) * stancArea;
                                    const wastePercentage = (
                                        (Math.floor(stanc.multiplier / bestSize) * wastePerSheet)
                                        + 
                                        ((stanc.multiplier % bestSize) > 0 ? sheetArea - (stanc.multiplier % bestSize) * stancArea : 0)
                                    ) / (sheetArea * Math.ceil(stanc.multiplier / bestSize)) * 100;

                                    return {
                                        stock: sheet,
                                        piecesPerSheet: bestSize,
                                        format: format as 'normal' | 'rotated',
                                        isEnough,
                                        sheetsNeededForExact,
                                        totalPiecesProduced,
                                        wastePerSheet,
                                        wastePercentage,
                                    };
                                }).filter(o => o.piecesPerSheet > 0);

                                allocations.sort((a, b) => a.wastePercentage - b.wastePercentage);

                                return {
                                    stanc,
                                    allocation: allocations[0],
                                };
                            });

                            setCalculationResult(res);
                        }}
                    >
                        <IconCalculator />
                    </Button>
                </Grid.Col>
            </Grid>
            {calculationResult.length > 0 && (
                <Grid>
                    <Grid.Col span={12}>
                        <Title order={3} mt="md">
                            <Translation base={[]} id="calculation-results" fallback="Eredmények" />
                        </Title>
                        <Divider my="sm" />
                        <Table striped highlightOnHover withBorder withColumnBorders>
                            <thead>
                                <tr>
                                    <th>Alapanyag</th>
                                    <th>Hiány/Többlet</th>
                                    <th>Szükséges</th>
                                    <th>Készlet</th>
                                </tr>
                            </thead>
                            <tbody>
                                {copy(calculationResult).reduce((all, {allocation}) => {
                                    if (allocation) {
                                        const index = all.findIndex((a) => `${a.stock.material.id}-${a.stock.width}-${a.stock.height}` === `${allocation.stock.material.id}-${allocation.stock.width}-${allocation.stock.height}`);
                                        if (index === -1) {
                                            all.push(allocation);
                                        } else {
                                            all[index].sheetsNeededForExact += allocation.sheetsNeededForExact;
                                        }
                                    }
                                    return all;
                                }, [] as AllocationType[]).sort((a, b) => a.stock.material.label.localeCompare(b.stock.material.label)).map((allocation) => {
                                    return (
                                        <tr key={`${allocation.stock.material.id}-${allocation.stock.width}-${allocation.stock.height}-${allocation.stock.multiplier}`}>
                                            <td>
                                                {getMaterialLabel(allocation.stock.material)} ({allocation.stock.width}x{allocation.stock.height})
                                            </td>
                                            <td>
                                                {allocation.stock.multiplier - allocation.sheetsNeededForExact >= 0 ? (
                                                    <span style={{ color: 'green', fontWeight: 'bold' }}>{allocation.stock.multiplier - allocation.sheetsNeededForExact} ív</span>
                                                ) : (
                                                    <span style={{ color: 'red', fontWeight: 'bold' }}>{allocation.stock.multiplier - allocation.sheetsNeededForExact} ív</span>
                                                )}
                                            </td>
                                            <td>
                                                {allocation.stock.multiplier - allocation.sheetsNeededForExact >= 0 ? (
                                                    <span style={{ color: 'green', fontWeight: 'bold' }}>{allocation.sheetsNeededForExact} ív</span>
                                                ) : (
                                                    <span style={{ color: 'red', fontWeight: 'bold' }}>{allocation.sheetsNeededForExact} ív</span>
                                                )}
                                            </td>
                                            <td>
                                                {allocation.stock.multiplier} ív
                                            </td>
                                        </tr>
                                    );
                                })}
                                {Object.entries(copy(stancList).filter((stanc) => stanc.width && stanc.height && !calculationResult.some(({stanc: s, allocation}) => allocation?.piecesPerSheet > 0 && s.id === stanc.id)).sort((a, b) => a.material.label.localeCompare(b.material.label)).reduce((all, stanc) => {
                                    const key = `${stanc.material.id}-${stanc.width}-${stanc.height}`;
                                    all[key] ??= { material: stanc.material, width: stanc.width, height: stanc.height, needed: 0 };
                                    all[key].needed += stanc.multiplier;
                                    return all;
                                }, {} as any)).map(([key, group]: any) => {
                                    return <tr key={key}>
                                        <td>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>
                                                {getMaterialLabel(group.material)} ({group.width}x{group.height})
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>{-group.needed} db</span>
                                        </td>
                                        <td>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>{group.needed} db</span>
                                        </td>
                                        <td>
                                            <span style={{ color: 'red', fontWeight: 'bold' }}>0 db</span>
                                        </td>
                                    </tr>;
                                })}
                            </tbody>
                        </Table>
                    </Grid.Col>
                </Grid>
            )}
        </>
    );
}

export default () => {
    return (
        <Title order={1}>
            <Translation id="stock" base={[]} fallback="Készlet" />
            <Stock />
        </Title>
    )
}